import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import './Modal.css'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  size?: 'small' | 'medium' | 'large'
  preventCloseOnOverlay?: boolean
}

export function Modal({ isOpen, onClose, children, size = 'medium', preventCloseOnOverlay = false }: ModalProps) {
  // Keep onClose in a ref so effects don't re-run on every render
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  // Track the history state we push so we can remove it on programmatic close
  const modalHistoryIdRef = useRef<string | null>(null)
  const wasOpenRef = useRef(false)
  const closedViaPopStateRef = useRef(false)

  useEffect(() => {
    // If the modal was open and is now closed (e.g. via X/Cancel/Escape),
    // remove the history entry we previously pushed (if it's still current).
    if (wasOpenRef.current && !isOpen) {
      const modalId = modalHistoryIdRef.current
      const historyState = window.history.state as unknown
      const currentModalId =
        historyState && typeof historyState === 'object'
          ? (historyState as any).__ordelioModalId
          : undefined

      if (!closedViaPopStateRef.current && modalId && currentModalId === modalId) {
        window.history.back()
      }

      modalHistoryIdRef.current = null
      closedViaPopStateRef.current = false
    }

    wasOpenRef.current = isOpen
  }, [isOpen])

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen])

  // Body scroll lock + back button interception
  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    closedViaPopStateRef.current = false
    const modalId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
    modalHistoryIdRef.current = modalId

    // Preserve existing history.state fields (react-router uses this)
    const existingState = window.history.state as unknown
    const nextState =
      existingState && typeof existingState === 'object'
        ? { ...(existingState as any), __ordelioModalId: modalId }
        : { __ordelioModalId: modalId }

    window.history.pushState(nextState, '')

    const handlePopState = () => {
      closedViaPopStateRef.current = true
      onCloseRef.current()
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('popstate', handlePopState)
    }
  }, [isOpen])

  if (!isOpen) return null

  const handleOverlayClick = () => {
    if (!preventCloseOnOverlay) {
      // Navigate back to remove the history entry we pushed
      window.history.back()
    }
  }

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div 
        className={`modal-content modal-${size}`} 
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

interface ModalHeaderProps {
  title: string
  onClose: () => void
  disabled?: boolean
}

export function ModalHeader({ title, onClose, disabled = false }: ModalHeaderProps) {
  return (
    <div className="modal-header">
      <h3>{title}</h3>
      <button onClick={onClose} className="close-btn" disabled={disabled}>
        ×
      </button>
    </div>
  )
}

interface ModalBodyProps {
  children: ReactNode
}

export function ModalBody({ children }: ModalBodyProps) {
  return <div className="modal-body">{children}</div>
}

interface ModalFooterProps {
  children: ReactNode
}

export function ModalFooter({ children }: ModalFooterProps) {
  return <div className="modal-footer">{children}</div>
}
